import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import SearchSessionResult from "./partials/SearchSessionResult";
import { generateRetestPDF } from "./partials/RetestCandidatesPDF"; // Chemin à adapter

import {
    getRetestCandidates as onGetRetestCandidates,
} from "store/actions";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import { Col, Row, Card, CardBody, Button, Badge, Container } from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";

const RetestCandidatesList = () => {
    document.title = "Liste des Candidats au Rattrapage | CinzanEdu";

    const dispatch = useDispatch();
    const { sessionUuid } = useParams();

    const [filters, setFilters] = useState({});
    const [studyLevelName, setStudyLevelName] = useState("");
    const [isLoading, setLoading] = useState(true);

    const selectFinalDecisionData = (state) => state.finalDecisionReducer;

    const RetestProperties = createSelector(
        [selectFinalDecisionData],
        (reducer) => ({
            candidates: reducer.retestCandidates || [], 
            sessionInfo: reducer.sessionInfo || null,
            loading:     reducer.loadingRetest || reducer.loading, 
        })
    );

    const { candidates, sessionInfo, loading } = useSelector(RetestProperties);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    useEffect(() => {
        if (sessionUuid) {
            dispatch(onGetRetestCandidates({ 
                session_uuid: sessionUuid,
                ...filters
            }));
        }
    }, [dispatch, sessionUuid, filters]);

    const handleSearch = (newFilters) => {
        setStudyLevelName(newFilters.study_level_label);
        setFilters({
            study_level_id: newFilters.study_level_id,
        });
    };

    const columns = useMemo(() => {
        // 1. Colonnes d'identité uniquement
        const baseColumns = [
            {
                header: "Matricule",
                accessorKey: "student_number",
                enableSorting: true,
            },
            {
                header: "Nom & Prénoms",
                accessorKey: "last_name",
                cell: (cell) => (
                    <span className="text-uppercase fw-medium">
                        {cell.row.original.last_name} {cell.row.original.first_name}
                    </span>
                ),
            },
            {
                header: "Sexe",
                accessorKey: "sex",
                enableSorting: true,
            },
        ];

        // 2. Extraction des titres de matières (EC)
        const ecTitles = new Set();
        candidates.forEach(cand => {
            if (cand.ec_results) {
                Object.keys(cand.ec_results).forEach(title => ecTitles.add(title));
            }
        });

        // 3. Génération des colonnes dynamiques (Matières)
        const dynamicEcColumns = Array.from(ecTitles).sort().map(title => ({
            header: title,
            accessorKey: `ec_results.${title}`,
            cell: (cell) => {
                const note = cell.getValue();
                if (!note || note === "VAL") return <span className="text-muted">-</span>;
                if (note === "ABS") return <Badge color="danger">ABS</Badge>;
                return <b className="text-danger">{parseFloat(note).toFixed(2)}</b>;
            },
        }));

        // On retourne l'identité suivie des matières, sans moyenne ni crédits
        return [...baseColumns, ...dynamicEcColumns];
    }, [candidates]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title="Rattrapage"
                        breadcrumbItem={
                            sessionInfo
                                ? `Liste des Admissibles — ${sessionInfo.label}`
                                : "Candidats au rattrapage"
                        }
                    />

                    <SearchSessionResult onSearch={handleSearch} />

                    {loading ? <Spinners setLoading={setLoading} /> :
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody className="border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div>
                                            <h5 className="mb-0 text-warning">Détail des matières à rattraper</h5>
                                            <p className="text-muted mb-0 small">
                                                Niveau : {studyLevelName || "Tous les niveaux"}
                                            </p>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Badge color="soft-warning" className="p-2 font-size-13">
                                                {candidates.length} étudiant(s)
                                            </Badge>
                                            
                                            <Button
                                                color="primary"
                                                outline
                                                // On passe les données nécessaires à la fonction
                                                onClick={() => generateRetestPDF({
                                                    results: candidates,
                                                    sessionInfo: sessionInfo,
                                                    studyLevelName: studyLevelName
                                                })}
                                            >
                                                <i className="bx bx-printer me-1"></i> Imprimer Liste
                                            </Button>
                                        </div>
                                    </CardBody>

                                    <CardBody>
                                        <div className="table-responsive">
                                            <TableContainer
                                                columns={columns}
                                                data={candidates || []}
                                                isCustomPageSize={true}
                                                isGlobalFilter={true}
                                                isPagination={true}
                                                SearchPlaceholder="Chercher un étudiant..."
                                                tableClass="align-middle table-nowrap table-hover"
                                                theadClass="table-light"
                                                pagination="pagination"
                                                paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                </Container>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default RetestCandidatesList;